//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import * as instagramUserApi from '@apis/instagram-user/instagramUserApi';
import {
  FEED_PREVIEW_LIVE_POST_LIMIT,
  IInstagramScheduleFeedPreviewState,
} from '@instagram-account/redux/schedule/feed-preview/instagramScheduleFeedPreviewModel';
import { IInstagramPost } from '@models/IInstagramMediaItem';
import { createThunk } from '@redux/createThunk';
import { SliceNames } from '@redux/SliceNames';
import normalize, { Normalized } from '@utils/normalize';
import { getRequestErrorMessage } from '@utils/getRequestErrorMessage';

const initialState: IInstagramScheduleFeedPreviewState = {
  errors: {
    fetchLivePostsError: undefined,
  },
  flags: {
    isFetchingLivePosts: false,
  },
  livePosts: Normalized.empty(),
};

const { actions, reducer } = createSlice({
  name: SliceNames.InstagramAccountScheduleFeedPreview,
  initialState,
  reducers: {
    requestLivePosts(state) {
      state.errors.fetchLivePostsError = undefined;
      state.flags.isFetchingLivePosts = true;
    },
    receiveLivePostsSucceeded(
      state,
      action: PayloadAction<Normalized<IInstagramPost, string>>,
    ) {
      state.flags.isFetchingLivePosts = false;
      state.livePosts = action.payload;
    },
    receiveLivePostsFailed(state, action: PayloadAction<string | undefined>) {
      state.flags.isFetchingLivePosts = false;
      state.errors.fetchLivePostsError = action.payload;
    },
  },
});

export const fetchLivePosts = createThunk(() => async (dispatch, getState) => {
  const { isFetchingLivePosts } =
    getState().instagram.schedule.feedPreview.flags;

  if (isFetchingLivePosts) {
    return;
  }

  dispatch(actions.requestLivePosts());

  try {
    const { items: livePosts } =
      await instagramUserApi.getDefaultInstagramUserLivePosts({
        limit: FEED_PREVIEW_LIVE_POST_LIMIT,
      });
    const normalizedLivePosts = normalize(livePosts, (post) => post.itemId);
    dispatch(actions.receiveLivePostsSucceeded(normalizedLivePosts));
  } catch (e) {
    dispatch(actions.receiveLivePostsFailed(getRequestErrorMessage(e)));
  }
});

export default reducer;
